import React from 'react';
import { Switch } from 'react-router-dom';
import SafetyRoute from 'components/kea/SafetyRoute';
import { Redirect } from 'react-router';
import { ROUTE_PATTERNS } from '../routeMap';
import SearchExperienceV2Settings from '../../../containers/SearchExperienceV2Settings';
import HomeScreen from '../../../containers/HomeScreen/HomeScreen';
import { CatalogHistoryPage } from '../../../features/catalog-sync';
import { SearchSettingsPage } from '../../../features/search-settings';
import LivePage from '../../../features/search-settings/LivePage';
import DiagnosticsScreen from '../../../containers/DiagnosticsScreen/DiagnosticsScreen';
import { PageAccessGate } from '../../../features/shell/context/PageAccessGate';
import HealthDashboardBridge from './analytic/HealthDashboard/IndexBridge';
import QueryPopularBridge from './analytic/QueryPopular/IndexBridge';
import QueryNoResultBridge from './analytic/QueryNoResult/IndexBridge';
import LostOpportunitiesBridge from './analytic/LostOpportunities/IndexBridge';
import QueryDetailBridge from './analytic/QueryDetail/IndexBridge';
import PopularProductsBridge from './analytic/PopularProducts/IndexBridge';
import CloudAnalyticsRouter from './analytic';
import Fields from './customization/fields/index';
import Filters from './customization/filters/index';
import EmptyQueryPage from './customization/emptyQuery/index';
import Synonyms from './customization/synonyms/index';
import SuggestionOverrides from './customization/suggestionOverrides/index';
import DataSettingsPage from '../../../features/data-settings/DataSettingsPage';
import DescriptionParametersPage from '../../../pluginAdmin/features/descriptionParameters/DescriptionParametersPage';
import CardTemplatePage from './customization/cardTemplate';
import SearchModePage from '../../../features/search-mode/SearchModePage';
import { useShellAppContext } from '../../../features/shell/context/useShellAppContext';
import CloudInterfaceAccessGate from '../../../features/operation-supervisor/CloudInterfaceAccessGate';
import MerchandisingRulesPage from '../../../features/merchandising/MerchandisingRulesPage';
import AgentMcpSettingsPage from '../../../pluginAdmin/features/agentMcp/AgentMcpSettingsPage';
import { StorefrontContentPage } from '../../../features/storefront-content';

const SearchRouter = ({ match }) => {
  const { url } = match;
  const { productProfile } = useShellAppContext();
  // Derive canonical sub-path suffixes from routeMap (strips the /app/:routeShopId/search mount prefix).
  // This ensures SearchRouter stays in sync with ROUTE_PATTERNS without duplicating path strings.
  const SEARCH_MOUNT = '/app/:routeShopId/search';
  const sub = (key) => ROUTE_PATTERNS[key].replace(SEARCH_MOUNT, '');
  const isCloud = productProfile?.id === 'cloud';
  const routeShopId = url.split('/')[2];
  return (
    <Switch>
      {/* No-capability routes: render regardless of session/capability state */}
      <SafetyRoute path={`${url}/home`} render={(props) => <PageAccessGate><HomeScreen {...props} /></PageAccessGate>} />
      <SafetyRoute path={`${url}/diagnostics`} render={(props) => <PageAccessGate><DiagnosticsScreen {...props} /></PageAccessGate>} />
      {/*
       * Capability-gated routes: fail closed (null) when capabilities absent.
       * In iframe mode ShellSessionGate triggers silent rebootstrap before these routes render.
       * In standalone mode (non-iframe / dev): capabilities are absent without a bootstrapped
       * shell session; null is intentional — these routes are unreachable standalone.
       */}
      <SafetyRoute
        path={`${url}/catalog-history`}
        render={(props) => <PageAccessGate requiresCapability="indexingV1"><CatalogHistoryPage {...props} /></PageAccessGate>}
      />
      {/* Analytics — bridge-backed (Task 10J) */}
      <SafetyRoute
        path={`${url}/analytic/query/:query`}
        render={(routeProps) => (
          <PageAccessGate requiresCapability="analyticsV1">
            <PageAccessGate requiresCapability="analyticsQueryDetailV1">
              <QueryDetailBridge {...routeProps} />
            </PageAccessGate>
          </PageAccessGate>
        )}
      />
      <SafetyRoute
        path={`${url}/analytic/health`}
        render={(props) => (
          <PageAccessGate requiresCapability="analyticsV1">
            <HealthDashboardBridge {...props} />
          </PageAccessGate>
        )}
      />
      <SafetyRoute
        path={`${url}/analytic`}
        render={(props) => (
          <PageAccessGate requiresCapability="analyticsV1">
            {isCloud ? (
              <CloudAnalyticsRouter {...props} />
            ) : (
              <Switch>
                <SafetyRoute path={`${url}/analytic/query-popular`} component={QueryPopularBridge} />
                <SafetyRoute path={`${url}/analytic/query-no-result`} component={QueryNoResultBridge} />
                <SafetyRoute path={`${url}/analytic/lost-opportunities`} component={LostOpportunitiesBridge} />
                <SafetyRoute
                  path={`${url}/analytic/popular-products`}
                  render={(routeProps) => (
                    <PageAccessGate requiresCapability="analyticsPopularProductsV1">
                      <PopularProductsBridge {...routeProps} />
                    </PageAccessGate>
                  )}
                />
              </Switch>
            )}
          </PageAccessGate>
        )}
      />
      {/* Search configuration — bridge-backed (Task 10J) */}
      <SafetyRoute
        path={`${url}${sub('searchFields')}`}
        render={(props) => <PageAccessGate requiresCapability="searchCustomizationV1"><Fields {...props} /></PageAccessGate>}
      />
      <SafetyRoute
        path={`${url}${sub('searchFilters')}`}
        render={(props) => <PageAccessGate requiresCapability="searchCustomizationV1"><Filters {...props} /></PageAccessGate>}
      />
      <SafetyRoute
        path={`${url}/customization/empty-query`}
        render={(props) => <PageAccessGate requiresCapability="emptyQueryV1"><EmptyQueryPage {...props} /></PageAccessGate>}
      />
      <SafetyRoute
        path={`${url}${sub('searchSynonyms')}`}
        render={(props) => <PageAccessGate requiresCapability="searchCustomizationV1"><Synonyms {...props} /></PageAccessGate>}
      />
      <SafetyRoute
        path={`${url}${sub('searchSuggestionOverrides')}`}
        render={(props) => (
          <PageAccessGate requiresCapability="suggestionOverridesV1">
            {isCloud ? <SuggestionOverrides {...props} /> : <Redirect to={`${url}/customization/synonyms`} />}
          </PageAccessGate>
        )}
      />
      <SafetyRoute
        path={`${url}${sub('searchTaxonomy')}`}
        render={() => <Redirect to={`${url}/customization/fields`} />}
      />
      {/* Epic 15: ProductCard template editor */}
      <SafetyRoute
        path={`${url}/customization/card-template`}
        render={(props) => (
          <PageAccessGate requiresCapability="cardTemplateEditor">
            <CloudInterfaceAccessGate pageId="cardTemplateEditor" shopId={routeShopId}>
              <CardTemplatePage {...props} />
            </CloudInterfaceAccessGate>
          </PageAccessGate>
        )}
      />
      {/* Merchandising — static workspace routes are resolved inside the page
          before its dynamic :ruleId editor route. */}
      <SafetyRoute
        path={`${url}/merchandising/rules`}
        render={(props) => (
          <PageAccessGate requiresCapability="merchandising_bridge_v1">
            <MerchandisingRulesPage {...props} />
          </PageAccessGate>
        )}
      />
      {/* Settings routes - order matters! More specific routes first */}
      <SafetyRoute
        path={`${url}/settings/ai`}
        render={(props) => (
          <PageAccessGate requiresCapability="agentMcpAdminV1">
            <AgentMcpSettingsPage {...props} />
          </PageAccessGate>
        )}
      />
      <SafetyRoute
        path={`${url}/settings/v2`}
        render={(props) => (
          <PageAccessGate requiresCapability="settingsV2">
            <CloudInterfaceAccessGate pageId="settingsV2" shopId={routeShopId}>
              <SearchExperienceV2Settings {...props} />
            </CloudInterfaceAccessGate>
          </PageAccessGate>
        )}
      />
      {/* Live — bridge-only standalone page (kill-switch toggle lives here).
          Routes directly to LivePage (not SearchSettingsPage) so the
          kill-switch remains reachable when no gateway/search-experience
          session is active. */}
      <SafetyRoute
        path={`${url}/settings/live`}
        render={(props) => (
          <PageAccessGate requiresCapability="operatorStateV1">
            <LivePage {...props} />
          </PageAccessGate>
        )}
      />
      {/* Theme — standalone page under Experience */}
      <SafetyRoute
        path={`${url}/settings/theme`}
        render={(props) => (
          <PageAccessGate requiresCapability="settingsV2">
            <CloudInterfaceAccessGate pageId="theme" shopId={routeShopId}>
              <SearchSettingsPage {...props} standaloneTabId="theme" />
            </CloudInterfaceAccessGate>
          </PageAccessGate>
        )}
      />
      {/* Integration — post-wizard search integration settings */}
      <SafetyRoute
        path={`${url}/settings/search-mode`}
        render={(props) => (
          <PageAccessGate requiresCapability="settingsV2">
            <CloudInterfaceAccessGate pageId="searchMode" shopId={routeShopId}>
              <SearchModePage {...props} />
            </CloudInterfaceAccessGate>
          </PageAccessGate>
        )}
      />
      <SafetyRoute
        path={`${url}/settings/storefront-content`}
        render={() => (
          <PageAccessGate requiresCapability="storefrontContentV1">
            <StorefrontContentPage />
          </PageAccessGate>
        )}
      />
      {/* Search Box — standalone page under UI Settings */}
      <SafetyRoute
        path={`${url}/settings/searchbox`}
        render={(props) => (
          <PageAccessGate requiresCapability="settingsV2">
            <CloudInterfaceAccessGate pageId="searchBoxSettings" shopId={routeShopId}>
              <SearchSettingsPage {...props} standaloneTabId="searchbox" />
            </CloudInterfaceAccessGate>
          </PageAccessGate>
        )}
      />
      {/* Results Page — standalone page under UI Settings */}
      <SafetyRoute
        path={`${url}/settings/result-page`}
        render={(props) => (
          <PageAccessGate requiresCapability="settingsV2">
            <CloudInterfaceAccessGate pageId="resultsPageSettings" shopId={routeShopId}>
              <SearchSettingsPage {...props} standaloneTabId="results-page" />
            </CloudInterfaceAccessGate>
          </PageAccessGate>
        )}
      />
      {/* Data settings — capability-gated dedicated page (T11) */}
      <SafetyRoute
        path={`${url}/data-settings`}
        render={(props) => (
          <PageAccessGate requiresCapability="catalogSelectorsV1">
            <DataSettingsPage {...props} />
          </PageAccessGate>
        )}
      />
      <SafetyRoute
        path={`${url}/settings/description-parameters`}
        render={() => isCloud ? <Redirect to={`/app/${routeShopId}/home`} /> : (
          <PageAccessGate requiresCapability="descriptionParametersV1">
            <DescriptionParametersPage />
          </PageAccessGate>
        )}
      />
      <SafetyRoute
        path={`${url}/settings/data-connectivity`}
        render={(props) => productProfile?.id === 'cloud' ? (
          <PageAccessGate requiresCapability="cloudDataSettingsV1">
            <SearchSettingsPage {...props} standaloneTabId="data" />
          </PageAccessGate>
        ) : <Redirect to={`${url}/data-settings`} />}
      />
      {/* Cloud retains crawler connectivity settings. Plugins use their
          authoritative product-selection page at /data-settings. */}
      <SafetyRoute
        path={`${url}/settings/data`}
        render={() => productProfile?.id === 'cloud'
          ? <Redirect to={`${url}/settings/data-connectivity`} />
          : <Redirect to={`${url}/data-settings`} />}
      />
      {/* Generic legacy settings tabs — Theme handled by exact standalone route above. */}
      <SafetyRoute
        path={`${url}${sub('searchSettings').replace(':tab', ':tabId')}`}
        render={(props) => <PageAccessGate requiresCapability="operatorStateV1"><SearchSettingsPage {...props} /></PageAccessGate>}
      />
      {/* Catch-all: redirect any unrecognized sub-path to canonical home */}
      <SafetyRoute component={() => <Redirect to={`${url}/home`} />} />
    </Switch>
  );
};

export default SearchRouter;
